import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HomeIcon, FileTextIcon, CreditCardIcon, BuildingIcon, UserIcon, BellIcon, HelpCircleIcon, LogOutIcon, ShieldCheckIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface UserSidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}
const UserSidebar: React.FC<UserSidebarProps> = ({
  isOpen,
  toggleSidebar
}) => {
  const location = useLocation();
  const {
    user,
    logout
  } = useAuth();
  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };
  const navItems = [{
    icon: <HomeIcon size={20} data-id="element-768" />,
    title: 'Dashboard',
    to: '/dashboard',
    isActive: location.pathname === '/dashboard'
  }, {
    icon: <FileTextIcon size={20} data-id="element-769" />,
    title: 'My Applications',
    to: '/my-applications',
    isActive: location.pathname === '/my-applications',
    badge: 2
  }, {
    icon: <CreditCardIcon size={20} data-id="element-770" />,
    title: 'Payment History',
    to: '/payment-history',
    isActive: location.pathname === '/payment-history'
  }, {
    icon: <BuildingIcon size={20} data-id="element-771" />,
    title: 'Government Services',
    to: '/mdas',
    isActive: location.pathname === '/mdas' || location.pathname.includes('/mdas/')
  }, {
    icon: <CalendarIcon size={20} data-id="element-772" />,
    title: 'Track Applications',
    to: '/track-application',
    isActive: location.pathname === '/track-application'
  }];
  const accountItems = [{
    icon: <UserIcon size={20} data-id="element-773" />,
    title: 'My Profile',
    to: '/profile',
    isActive: location.pathname === '/profile'
  }, {
    icon: <BellIcon size={20} data-id="element-774" />,
    title: 'Notifications',
    to: '/notifications',
    isActive: location.pathname === '/notifications',
    badge: 1
  }, {
    icon: <HelpCircleIcon size={20} data-id="element-775" />,
    title: 'Help & Support',
    to: '/support',
    isActive: location.pathname === '/support'
  }];
  const sidebarClasses = isOpen ? 'w-64 transform translate-x-0' : 'w-20 transform translate-x-0';
  return <div className={`bg-white h-full border-r border-gray-200 transition-all duration-300 ease-in-out hidden lg:block ${sidebarClasses} relative`} data-id="element-776">
      {/* Collapse toggle button */}
      <button onClick={toggleSidebar} className="absolute -right-3 top-20 bg-white border border-gray-200 rounded-full p-1.5 shadow-md z-10 hover:bg-gray-50" aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'} data-id="element-777">
        {isOpen ? <ChevronLeftIcon size={16} className="text-gray-600" data-id="element-778" /> : <ChevronRightIcon size={16} className="text-gray-600" data-id="element-779" />}
      </button>
      <div className="h-full flex flex-col" data-id="element-780">
        {/* Header/Logo */}
        <div className={`px-4 py-6 border-b border-gray-200 ${!isOpen && 'flex justify-center'}`} data-id="element-781">
          <div className="flex items-center space-x-3" data-id="element-782">
            <div className="bg-green-50 p-1 rounded-md flex items-center justify-center" data-id="element-783">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-8 w-8" data-id="element-784" />
            </div>
            {isOpen && <div data-id="element-785">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-786">
                  Edo State
                </h2>
                <p className="text-xs text-gray-500" data-id="element-787">E-Services Portal</p>
              </div>}
          </div>
        </div>
        {/* User Profile */}
        <div className={`px-4 py-4 border-b border-gray-200 ${!isOpen && 'flex justify-center'}`} data-id="element-788">
          <div className={`flex items-center ${isOpen ? 'space-x-3' : 'justify-center'}`} data-id="element-789">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold" data-id="element-790">
              {user?.name?.charAt(0) || 'A'}
            </div>
            {isOpen && <div data-id="element-791">
                <p className="text-sm font-medium text-gray-900" data-id="element-792">
                  {user?.name || 'Ajibola Oluga'}
                </p>
                <p className="text-xs text-gray-500" data-id="element-793">
                  {user?.email || 'ajibola.oluga@example.com'}
                </p>
              </div>}
          </div>
        </div>
        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4 px-3" data-id="element-794">
          <div className="space-y-1" data-id="element-795">
            {navItems.map((item, index) => <Link key={index} to={item.to} className={`flex items-center justify-between px-4 py-3 text-sm transition-colors rounded-md ${item.isActive ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-green-50 hover:text-green-700'} ${!isOpen && 'justify-center'}`} title={!isOpen ? item.title : undefined} data-id="element-796">
                <div className="flex items-center" data-id="element-797">
                  <span className={`${item.isActive ? 'text-green-600' : 'text-gray-500'} ${isOpen ? 'mr-3' : ''}`} data-id="element-798">
                    {item.icon}
                  </span>
                  {isOpen && <span data-id="element-799">{item.title}</span>}
                </div>
                {isOpen && item.badge ? <span className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full" data-id="element-800">
                    {item.badge}
                  </span> : null}
              </Link>)}
          </div>
          <div className={`mt-6 pt-6 border-t border-gray-200 ${!isOpen && 'text-center'}`} data-id="element-801">
            {isOpen && <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-802">
                Account
              </h3>}
            <div className="space-y-1" data-id="element-803">
              {accountItems.map((item, index) => <Link key={index} to={item.to} className={`flex items-center justify-between px-4 py-3 text-sm transition-colors rounded-md ${item.isActive ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-green-50 hover:text-green-700'} ${!isOpen && 'justify-center'}`} title={!isOpen ? item.title : undefined} data-id="element-804">
                  <div className="flex items-center" data-id="element-805">
                    <span className={`${item.isActive ? 'text-green-600' : 'text-gray-500'} ${isOpen ? 'mr-3' : ''}`} data-id="element-806">
                      {item.icon}
                    </span>
                    {isOpen && <span data-id="element-807">{item.title}</span>}
                  </div>
                  {isOpen && item.badge ? <span className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full" data-id="element-808">
                      {item.badge}
                    </span> : null}
                </Link>)}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className={`p-4 border-t border-gray-200 ${!isOpen && 'text-center'}`} data-id="element-809">
          {isOpen && <div className="flex items-center px-4 py-2 mb-4 bg-green-50 text-green-700 rounded-md" data-id="element-810">
              <ShieldCheckIcon size={18} className="mr-2" data-id="element-811" />
              <span className="text-xs font-medium" data-id="element-812">Secure Connection</span>
            </div>}
          <button onClick={handleLogout} className={`flex items-center ${isOpen ? 'w-full px-4' : 'justify-center w-full'} py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors`} title={!isOpen ? 'Logout' : undefined} data-id="element-813">
            <LogOutIcon size={20} className={isOpen ? 'mr-3' : ''} data-id="element-814" />
            {isOpen && 'Logout'}
          </button>
        </div>
      </div>
    </div>;
};
export default UserSidebar;